import type { KeyboardEvent, ReactNode } from "react"
import { useViewportWidth } from "../hooks/use-viewport.ts"
import { Icon } from "./icons.tsx"
import { cardColumns, isCardLayout, visibleColumns, type Column } from "./table-columns.ts"

/** 지금 정렬 상태와, 머리글을 눌렀을 때 할 일. 판단은 `lib/table-sort.ts` 가 한다. */
export type TableSort = {
  key: string | null
  dir: "asc" | "desc"
  onSort: (key: string) => void
  /** 스크린리더용 — "정렬" 같은 한 마디. */
  label: string
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  density = "normal",
  footer,
  sort,
}: {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  /** 행을 누르면 여는 동작. 주면 행 전체가 버튼처럼 동작한다. */
  onRowClick?: (row: T) => void
  empty?: ReactNode
  density?: "normal" | "compact"
  footer?: ReactNode
  /** 주면 `sortKey` 가 있는 칸의 머리글이 누를 수 있는 단추가 된다. */
  sort?: TableSort
}) {
  const width = useViewportWidth()
  const shown = visibleColumns(columns, width)

  if (rows.length === 0) {
    return (
      <>
        {empty ?? null}
        {footer ?? null}
      </>
    )
  }

  function onRowKeyDown(event: KeyboardEvent<HTMLElement>, row: T) {
    if (!onRowClick) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onRowClick(row)
    }
  }

  if (isCardLayout(width)) {
    const forCards = cardColumns(columns)
    const primary = forCards.find((column) => column.primary) ?? forCards[0]
    const rest = forCards.filter((column) => column !== primary)
    return (
      <>
        <ul className="cards-list">
          {rows.map((row) => (
            <li
              key={rowKey(row)}
              className={`row-card${onRowClick ? " is-clickable" : ""}`}
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={(event) => onRowKeyDown(event, row)}
            >
              <div className="row-card__title">{primary.render(row)}</div>
              <dl className="row-card__defs">
                {rest.map((column) => (
                  <div key={column.key} className="row-card__def">
                    <dt>{column.header}</dt>
                    <dd>{column.render(row)}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
        {footer ?? null}
      </>
    )
  }

  return (
    <>
      <div className="table-wrap">
        <table className={density === "compact" ? "table table--compact" : "table"}>
          <thead>
            <tr>
              {shown.map((column) => {
                const sortable = sort && column.sortKey
                const active = sortable && sort.key === column.sortKey
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.align === "end" ? "num" : undefined}
                    aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className={active ? "th-sort is-active" : "th-sort"}
                        title={sort.label}
                        onClick={() => sort.onSort(column.sortKey!)}
                      >
                        {column.header}
                        <Icon
                          name={active ? (sort.dir === "asc" ? "sortAsc" : "sortDesc") : "sortNone"}
                          size={13}
                          className="th-sort__icon"
                        />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? "is-clickable" : undefined}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={(event) => onRowKeyDown(event, row)}
              >
                {shown.map((column) => (
                  <td key={column.key} className={column.align === "end" ? "num" : undefined}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ?? null}
    </>
  )
}
